import { Link } from 'react-router-dom';
import useAuthRedirect from '../hooks/useAuthRedirect';
import { getCurrentUser } from '../services/telegram-auth';
import { useEffect, useRef, useState } from 'react';
import { FiPlayCircle, FiArrowRight } from 'react-icons/fi';
import { BsTelegram } from 'react-icons/bs';

const HomePage = () => {
    // Используем хук для перенаправления авторизованных пользователей
    useAuthRedirect();

    // Проверяем, авторизован ли пользователь
    const currentUser = getCurrentUser();

    // Состояние для контроля загрузки видео
    const [videoLoaded, setVideoLoaded] = useState(false);
    const [videoError, setVideoError] = useState(false);
    const videoRef = useRef<HTMLVideoElement>(null);
    const [isMobile, setIsMobile] = useState(false);

    // Определяем тип устройства при загрузке и при изменении размера окна
    useEffect(() => {
        const checkIsMobile = () => {
            const mobile = window.innerWidth < 768 || 
                        /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
            setIsMobile(mobile);
            console.log('Устройство определено как:', mobile ? 'мобильное' : 'десктопное');
        };

        // Проверяем при загрузке
        checkIsMobile();

        // Слушаем изменение размера окна
        window.addEventListener('resize', checkIsMobile);

        return () => {
            window.removeEventListener('resize', checkIsMobile);
        };
    }, []);

    // Обработчик загрузки видео
    useEffect(() => {
        const videoElement = videoRef.current;
        if (videoElement) {
            const handleLoadedData = () => {
                console.log('Видео загружено успешно');
                setVideoLoaded(true);
                setVideoError(false);
            };

            const handleError = (e: Event) => {
                console.error('Ошибка загрузки видео:', e);
                setVideoError(true);
            };

            videoElement.addEventListener('loadeddata', handleLoadedData);
            videoElement.addEventListener('error', handleError);

            // Принудительная попытка воспроизведения
            videoElement.load();
            const playPromise = videoElement.play();
            if (playPromise !== undefined) {
                playPromise.catch(error => {
                    console.warn('Автовоспроизведение заблокировано:', error);
                    // Не устанавливаем videoError в true, чтобы не показывать fallback-изображение,
                    // так как видео может воспроизвестись при взаимодействии пользователя
                });
            }

            return () => {
                videoElement.removeEventListener('loadeddata', handleLoadedData);
                videoElement.removeEventListener('error', handleError);
            };
        }
    }, [isMobile]);

    // Если пользователь авторизован, показываем заглушку
    // Это на случай, если перенаправление еще не произошло
    if (currentUser) {
        return (
            <div className="flex justify-center items-center py-12">
                <div className="text-center">
                    <div className="w-10 h-10 mx-auto rounded-full border-2 border-primary border-t-transparent animate-spin mb-4"></div>
                    <p className="text-apple-500">Перенаправление на страницу оборудования...</p>
                </div>
            </div>
        );
    }

    // Статичное фоновое изображение для запасного варианта
    const fallbackImage = "https://images.unsplash.com/photo-1581094794329-c8112a89f47f?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80";

    return (
        <div className="fixed inset-0 overflow-hidden">
            {/* Фоновое видео на всю страницу */}
            <div className="absolute top-0 left-0 w-full h-full z-0 bg-apple-800">
                {/* Показываем статичное изображение только при ошибке загрузки видео */}
                {videoError && (
                    <img
                        src={fallbackImage}
                        alt="Техника для аренды"
                        className="w-full h-full object-cover opacity-70"
                    />
                )}
                
                <video
                    ref={videoRef}
                    className={`w-full h-full object-cover transition-opacity duration-700 ${videoLoaded ? 'opacity-70' : 'opacity-0'} ${videoError ? 'hidden' : ''}`}
                    autoPlay
                    muted
                    loop
                    playsInline={true}
                    preload="auto"
                >
                    {isMobile ? (
                        <>
                            <source src="/videos/bg-rental_2.mp4" type="video/mp4" />
                            <source src="/videos/bg-rental_2.webm" type="video/webm" />
                        </>
                    ) : (
                        <>
                            <source src="/videos/bg-rental_2.webm" type="video/webm" />
                            <source src="/videos/bg-rental_2.mp4" type="video/mp4" />
                        </>
                    )}
                </video>
            </div>

            {/* Наложение для улучшения читаемости */}
            <div className="absolute top-0 left-0 w-full h-full z-[1] bg-gradient-to-r from-apple-900/80 to-apple-800/50"></div>

            {/* Центрированный контент */}
            <div className="absolute inset-0 z-[2] flex items-center justify-center">
                <div className="container mx-auto px-4 sm:px-6 text-center max-w-screen-xl">
                    <h1 className="text-3xl sm:text-5xl md:text-5xl font-bold tracking-tight text-white mb-2 sm:mb-3">
                        <span className="block">Видео и фото техника</span>
                        <span className="block mt-1">
                            <span className="hidden sm:inline">для съемок</span>
                            <span className="text-primary relative inline-block sm:ml-2">
                                в Калининграде
                            </span>
                        </span>
                    </h1>

                    <p className="text-sm sm:text-base md:text-lg text-gray-200 mb-3 sm:mb-5 max-w-xl mx-auto font-light">
                        Профессионального оборудования для задач любой сложности
                    </p>

                    <div className="flex xs:flex-row flex-col gap-2 xs:gap-3 sm:gap-4 justify-center max-w-md mx-auto">
                        <Link
                            to="/equipment"
                            className="group text-xs sm:text-sm px-3 sm:px-5 py-2 text-white bg-primary hover:bg-primary-600 rounded-lg shadow-md hover:shadow-lg transition-all duration-300 font-medium flex items-center justify-center"
                        >
                            <FiPlayCircle className="mr-1 text-lg" />
                            <span className="inline xxs:inline">Выбрать оборудование</span>
                            <FiArrowRight className="ml-1 transform group-hover:translate-x-1 transition-transform" />
                        </Link>
                        <Link
                            to="/login"
                            className="text-xs sm:text-sm px-3 sm:px-5 py-2 bg-white/10 backdrop-blur-sm text-white rounded-lg hover:bg-white/20 transition-all duration-300 font-medium border border-white/30 flex items-center justify-center"
                        >
                            <BsTelegram className="mr-1 text-lg" />
                            <span>Войти через Telegram</span>
                        </Link>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default HomePage; 